import { useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
} from "react-bootstrap";

const GREEN = "#04aa00";

export default function ListEditor({
  title,
  addLabel,
  items,
  setItems,
  setDeletedIds,
  fields,
  emptyItem,
  getTitle,
  getSubtitle,
}) {
  const [editingIndex, setEditingIndex] =
    useState(null);

  const addItem = () => {
    const index = items.length;

    setItems((previous) => [
      ...previous,
      {
        ...emptyItem,
        jarjestys: index,
      },
    ]);

    setEditingIndex(index);
  };

  const updateItem = (
    index,
    field,
    value
  ) => {
    setItems((previous) =>
      previous.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const deleteItem = (index) => {
    const item = items[index];

    if (
      !window.confirm(
        "Haluatko varmasti poistaa rivin?"
      )
    ) {
      return;
    }

    if (item.id && setDeletedIds) {
      setDeletedIds((previous) => [
        ...previous,
        item.id,
      ]);
    }

    setItems((previous) =>
      previous
        .filter((_, i) => i !== index)
        .map((item, newIndex) => ({
          ...item,
          jarjestys: newIndex,
        }))
    );

    setEditingIndex(null);
  };

  const moveItem = (
    fromIndex,
    toIndex
  ) => {
    if (
      toIndex < 0 ||
      toIndex >= items.length ||
      fromIndex === toIndex
    ) {
      return;
    }

    setItems((previous) => {
      const next = [...previous];

      const [moved] = next.splice(
        fromIndex,
        1
      );

      next.splice(
        toIndex,
        0,
        moved
      );

      return next.map(
        (item, index) => ({
          ...item,
          jarjestys: index,
        })
      );
    });

    setEditingIndex((current) => {
      if (current === null) {
        return current;
      }

      if (current === fromIndex) {
        return toIndex;
      }

      if (
        fromIndex < toIndex &&
        current > fromIndex &&
        current <= toIndex
      ) {
        return current - 1;
      }

      if (
        fromIndex > toIndex &&
        current >= toIndex &&
        current < fromIndex
      ) {
        return current + 1;
      }

      return current;
    });
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5
          className="mb-0"
          style={{
            color: "#315b31",
          }}
        >
          {title}
        </h5>

        <Button
          type="button"
          size="sm"
          onClick={addItem}
          style={{
            backgroundColor: GREEN,
            borderColor: GREEN,
          }}
        >
          + {addLabel}
        </Button>
      </div>

      <div className="d-flex flex-column gap-3">
        {items.map(
          (item, index) => {
            const editing =
              editingIndex === index;

            return (
              <Card
                key={
                  item.id ??
                  `new-${index}`
                }
                style={{
                  border:
                    "1px solid #e3e9e3",
                  borderLeft:
                    `5px solid ${GREEN}`,
                  borderRadius:
                    "12px",
                  boxShadow:
                    "0 3px 10px rgba(0,0,0,0.06)",
                }}
              >
                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor:
                      "#f7fbf7",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius:
                          "50%",
                        backgroundColor:
                          GREEN,
                        color:
                          "white",
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        fontWeight:
                          "bold",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </div>

                    <div>
                      <div
                        className="fw-semibold"
                        style={{
                          color:
                            "#315b31",
                        }}
                      >
                        {getTitle?.(
                          item
                        ) ||
                          `Rivi ${
                            index + 1
                          }`}
                      </div>

                      {getSubtitle && (
                        <small className="text-muted">
                          {getSubtitle(
                            item
                          )}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      disabled={
                        index === 0
                      }
                      onClick={() =>
                        moveItem(
                          index,
                          index - 1
                        )
                      }
                      title="Siirrä ylöspäin"
                    >
                      ↑
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      disabled={
                        index ===
                        items.length - 1
                      }
                      onClick={() =>
                        moveItem(
                          index,
                          index + 1
                        )
                      }
                      title="Siirrä alaspäin"
                    >
                      ↓
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      style={
                        editing
                          ? {
                              backgroundColor:
                                GREEN,
                              borderColor:
                                GREEN,
                              color:
                                "#ffffff",
                            }
                          : {}
                      }
                      onClick={() =>
                        setEditingIndex(
                          editing
                            ? null
                            : index
                        )
                      }
                    >
                      {editing
                        ? "Valmis"
                        : "Muokkaa"}
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      onClick={() =>
                        deleteItem(
                          index
                        )
                      }
                    >
                      Poista
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body>
                  <Row className="g-3">
                    {fields.map(
                      (field) => (
                        <Col
                          key={
                            field.name
                          }
                          xs={12}
                          md={
                            field.md ||
                            4
                          }
                        >
                          <div
                            className="text-muted fw-semibold"
                            style={{
                              fontSize:
                                "12px",
                              marginBottom:
                                "4px",
                            }}
                          >
                            {
                              field.label
                            }
                          </div>

                          {editing ? (
                            field.options ? (
                              <Form.Select
                                value={
                                  item[
                                    field
                                      .name
                                  ] || ""
                                }
                                onChange={(
                                  e
                                ) =>
                                  updateItem(
                                    index,
                                    field.name,
                                    e
                                      .target
                                      .value
                                  )
                                }
                                style={{
                                  borderRadius:
                                    "8px",
                                }}
                              >
                                {field.placeholder && (
                                  <option value="">
                                    {
                                      field.placeholder
                                    }
                                  </option>
                                )}

                                {field.options.map(
                                  (
                                    option
                                  ) => (
                                    <option
                                      key={
                                        option
                                      }
                                      value={
                                        option
                                      }
                                    >
                                      {
                                        option
                                      }
                                    </option>
                                  )
                                )}
                              </Form.Select>
                            ) : (
                              <Form.Control
                                type={
                                  field.type ||
                                  "text"
                                }
                                value={
                                  item[
                                    field
                                      .name
                                  ] || ""
                                }
                                onChange={(
                                  e
                                ) =>
                                  updateItem(
                                    index,
                                    field.name,
                                    e
                                      .target
                                      .value
                                  )
                                }
                                style={{
                                  borderRadius:
                                    "8px",
                                }}
                              />
                            )
                          ) : (
                            <div>
                              {item[
                                field
                                  .name
                              ] || "–"}
                            </div>
                          )}
                        </Col>
                      )
                    )}
                  </Row>
                </Card.Body>
              </Card>
            );
          }
        )}
      </div>
    </>
  );
}